import React, { FC, ChangeEvent, useContext, useState, useEffect } from "react";
import { Collapse, FormControl, FormControlLabel, FormLabel, FormGroup } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { FiltersContext, VisibilityContext } from "../../context";
import { FILTERS } from "../../constants";
import StyledCheckbox from "../Common/StyledCheckbox";
import { escapeRegExp } from "lodash";
import StyledTooltip from "../Common/StyledTooltip";
import TooltipHelpIcon from "../Common/TooltipHelpIcon";

const useStyles = makeStyles({
  formGroup: {
    paddingLeft: "3px",
  },
  nested: {
    paddingLeft: 10,
    fontSize: "0.9rem",
  },
  toggle: {
    fontSize: "1.25em",
    fontWeight: "bold",
    display: "inline-block",
    paddingLeft: 5,
    width: 25,
  },
  tooltip: {
    maxWidth: 400,
  },
});

type CheckboxesProps = {
  section: string;
  label?: string;
  visibilitySection: string;
  tooltips?: Record<string, string | Record<string, string>>;
};

const Checkboxes: FC<CheckboxesProps> = ({ section, label, visibilitySection, tooltips = {} }) => {
  const classes = useStyles();

  const [filtersContext, setFiltersContext] = useContext(FiltersContext);
  const [visibilityContext] = useContext(VisibilityContext);
  const [selectAll, setSelectAll] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const selectedCheckbox = event.target.name;

    if (selectedCheckbox === "All") {
      setSelectAll(!selectAll);
      setFiltersContext((prev) => {
        const newFilters = checked ? FILTERS.OPTIONS[section] : [];
        return {
          ...prev,
          [section]: newFilters,
        };
      });
    } else {
      setFiltersContext((prev) => {
        const newFilters = checked
          ? [...prev[section], selectedCheckbox]
          : prev[section].filter((section) => section !== selectedCheckbox);
        return {
          ...filtersContext,
          [section]: newFilters,
        };
      });
    }
  };

  useEffect(() => {
    const allFiltersApplied = FILTERS.OPTIONS[section].every((option) => filtersContext[section].includes(option));
    setSelectAll(allFiltersApplied);
  });

  return (
    <Collapse in={visibilityContext[visibilitySection]}>
      <FormControl component="fieldset" margin="dense">
        {label && <FormLabel component="legend">{label}</FormLabel>}
        <FormGroup className={classes.formGroup}>
          <FormControlLabel
            key="All"
            label="All"
            control={
              <StyledCheckbox
                name="All"
                checked={selectAll}
                onChange={(event, checked) => handleChange(event, checked)}
              />
            }
          />
          {FILTERS.OPTIONS[section].map((option) => (
            <CheckboxItem
              key={option?.name ?? option}
              option={option}
              tooltip={tooltips[option?.name ?? option]}
              section={section}
              sectionFilters={filtersContext[section]}
              handleChange={handleChange}
            />
          ))}
        </FormGroup>
      </FormControl>
    </Collapse>
  );
};

const CheckboxItem: FC<{
  option: string | { name: string; values: string[] };
  section: string;
  sectionFilters: string[];
  handleChange: (event, checked) => void;
  tooltip?: string | Record<string, string>;
}> = ({ option, section, sectionFilters, handleChange, tooltip }) => {
  const classes = useStyles();
  const [nestedExpanded, setNestedExpanded] = useState(false);

  if (typeof option === "string") {
    const label = tooltip ? (
      <StyledTooltip classes={{ tooltip: classes.tooltip }} title={tooltip}>
        <span>{option}</span>
      </StyledTooltip>
    ) : (
      option
    );
    return (
      <FormControlLabel
        key={option}
        label={label}
        control={<StyledCheckbox name={option} checked={sectionFilters.includes(option)} onChange={handleChange} />}
      />
    );
  } else {
    const isChildSelected =
      !sectionFilters.includes(option.name) && option.values.some((v) => sectionFilters.includes(v));
    const isNestedVisible = nestedExpanded || isChildSelected;
    const handleParentClick = (e) => {
      e.preventDefault();
      if (isChildSelected) return;
      setNestedExpanded((prev) => !prev);
    };

    const parentTooltip = tooltip?.[option.name];
    return (
      <>
        <FormControlLabel
          key={option.name}
          label={
            <>
              <span>{option.name}</span>
              {parentTooltip && <TooltipHelpIcon tooltip={parentTooltip} />}
              <span className={classes.toggle} onClick={handleParentClick}>
                {isNestedVisible ? "-" : "+"}
              </span>
            </>
          }
          control={
            <StyledCheckbox
              name={option.name}
              checked={sectionFilters.includes(option.name)}
              onChange={(event, checked) => {
                handleChange(event, checked);
                option.values.forEach((v) => handleChange({ target: { name: v } }, checked));
              }}
            />
          }
        />
        {isNestedVisible &&
          option.values.map((v) => {
            const childTooltip = tooltip?.[v];
            const labelText =
              section === "complexForms" ? v.replace(new RegExp(`^${escapeRegExp(option.name)}: `), "") : ""; // remove the beginning of the name, since that's the parent
            const label = childTooltip ? (
              <StyledTooltip classes={{ tooltip: classes.tooltip }} title={childTooltip}>
                <span>{labelText}</span>
              </StyledTooltip>
            ) : (
              labelText
            );
            return (
              <FormControlLabel
                key={v}
                label={label}
                className={classes.nested}
                control={<StyledCheckbox name={v} checked={sectionFilters.includes(v)} onChange={handleChange} />}
              />
            );
          })}
      </>
    );
  }
};

export default Checkboxes;
